import type {
  AnalystOpinionResponse,
  ContactResponse,
  DocumentManagerDocumentResponse,
  DocumentRequirementFileResponse,
  DocumentRequirementResponse,
  DocumentRequirementStatusResponse,
  DocumentRequirementsAnalysisResponse,
  DocumentRequirementsAnalysisStatusResponse,
  DocumentRequirementsProgressResponse,
} from 'services/actions/ai_management_document_requirements';

export type DocumentRequirementStatus = DocumentRequirementStatusResponse;

export interface DocumentRequirementFile {
  id: number;
  name: string;
  origin: DocumentRequirementFileResponse['origin'];
  expiresOn: string | null;
  createdBy: string | null;
  createdAt: string;
  downloadUrl: string;
  sourceDocumentId: string | null;
}

export interface AnalystOpinionCandidate {
  id: string;
  name: string;
  origin: string | null;
  group: string | null;
  status: string | null;
  expiresOn: string | null;
  level: string | null;
  downloadUrl: string | null;
}

export const CANDIDATE_ORIGIN_LABEL: Record<string, string> = {
  upload: 'Adicionado no Gerenciar Documentos',
  idocumentei: 'Coletado automaticamente pelo iDocumentei',
};

const LEVEL_ORDER: Record<string, number> = { alta: 0, média: 1, media: 1, baixa: 2 };

export interface AnalystOpinion {
  level: AnalystOpinionResponse['compatibilidade'];
  recommendedName: string | null;
  recommendedId: string | null;
  justification: string | null;
  pendencies: string[];
  candidates: AnalystOpinionCandidate[];
}

export interface DocumentRequirement {
  id: number;
  kind: 'document' | 'declaration';
  source: 'edict' | 'manual';
  status: DocumentRequirementStatus;
  title: string;
  description: string | null;
  edictExcerpt: string | null;
  lowConfidence: boolean;
  confirmedBy: string | null;
  ignoredBy: string | null;
  analystNote: string | null;
  noDocumentFound: boolean;
  analystOpinion: AnalystOpinion | null;
  files: DocumentRequirementFile[];
}

export interface DocumentRequirementsProgress {
  total: number;
  confirmed: number;
  inReview: number;
  pending: number;
  ignored: number;
  percentage: number;
}

export interface DocumentManagerDocument {
  id: string;
  name: string;
  group: string;
  expiresOn: string | null;
  expired: boolean;
}

export const EMPTY_PROGRESS: DocumentRequirementsProgress = {
  total: 0,
  confirmed: 0,
  inReview: 0,
  pending: 0,
  ignored: 0,
  percentage: 0,
};

export const DOCUMENT_REQUIREMENT_STATUS: Record<
  DocumentRequirementStatus,
  { label: string; badge: string }
> = {
  pending: { label: 'Documento pendente', badge: 'danger' },
  in_review: { label: 'Aguardando revisão', badge: 'warning' },
  confirmed: { label: 'Documento confirmado', badge: 'success' },
  ignored: { label: 'Exigência ignorada', badge: 'secondary' },
};

export const FILE_ORIGIN_LABEL: Record<DocumentRequirementFile['origin'], string> = {
  upload: 'Adicionado do computador',
  document_manager: 'Importado do Gerenciar Documentos',
  analyst: 'Encontrado pelo Analista de Habilitação',
  declaration: 'Declaração preenchida',
};

export type DocumentRequirementsAnalysisStatus = DocumentRequirementsAnalysisStatusResponse;

export interface DocumentRequirementsAnalysis {
  status: DocumentRequirementsAnalysisStatus;
  warnings: string[];
  errorMessage: string | null;
}

const ANALYSIS_STATUSES: DocumentRequirementsAnalysisStatus[] = [
  'processing',
  'partial',
  'done',
  'error',
];

const ANALYST_UNAVAILABLE = 'O Analista de Habilitação está indisponível no momento.';

const ANALYSIS_ERROR_MESSAGE: Record<string, string> = {
  indisponivel: ANALYST_UNAVAILABLE,
  armazenamento_indisponivel: ANALYST_UNAVAILABLE,
  timeout: 'A análise demorou mais que o esperado e foi interrompida.',
  resumo_nao_encontrado: 'O resumo do edital desta licitação ainda não está disponível.',
  resumo_invalido: 'Não foi possível ler o resumo do edital desta licitação.',
};

export const ANALYSIS_ERROR_FALLBACK = 'Não foi possível concluir a análise dos documentos.';

const ANALYSIS_WARNING: Record<string, string> = {
  juiz_degradado: 'Algumas exigências ficaram sem parecer do Analista.',
  juiz_prazo_estourado:
    'A análise atingiu o tempo limite e algumas exigências ficaram sem parecer.',
  ranking_degradado: 'A busca por documentos semelhantes ficou limitada nesta análise.',
  reforco_degradado: 'Não foi possível consultar o edital completo para algumas exigências.',
  sem_exigencias: 'Nenhuma exigência foi enviada para análise.',
};

// Avisos do serviço são códigos em snake_case; um código ainda sem tradução não vai para a tela.
const SERVICE_CODE = /^[a-z_]+$/;

const ownValue = (map: Record<string, string>, key: string | null | undefined) =>
  key && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

const toWarning = (aviso: unknown) => {
  if (typeof aviso !== 'string' || !aviso.trim()) return null;
  return ownValue(ANALYSIS_WARNING, aviso) ?? (SERVICE_CODE.test(aviso) ? null : aviso);
};

export const toAnalysis = (
  analysis: DocumentRequirementsAnalysisResponse | null | undefined,
): DocumentRequirementsAnalysis | null => {
  if (!analysis || !ANALYSIS_STATUSES.includes(analysis.status)) return null;

  const warnings = (Array.isArray(analysis.avisos) ? analysis.avisos : [])
    .map(toWarning)
    .filter((warning): warning is string => !!warning);

  return {
    status: analysis.status,
    warnings: Array.from(new Set(warnings)),
    errorMessage:
      analysis.status === 'error'
        ? ownValue(ANALYSIS_ERROR_MESSAGE, analysis.motivo) ?? ANALYSIS_ERROR_FALLBACK
        : null,
  };
};

export const ANALYSIS_TIMED_OUT: DocumentRequirementsAnalysis = {
  status: 'error',
  warnings: [],
  errorMessage: ANALYSIS_ERROR_MESSAGE.timeout,
};

const contactName = (contact: ContactResponse | null) => contact?.name || null;

const toAnalystOpinion = (opinion?: AnalystOpinionResponse | null): AnalystOpinion | null => {
  if (!opinion) return null;
  const recommended = opinion.recomendado
    ? (opinion.candidatos ?? []).find((candidate) => candidate.id === opinion.recomendado)
    : undefined;
  return {
    level: opinion.compatibilidade,
    recommendedName: recommended?.nome || null,
    recommendedId: recommended?.id || null,
    justification: opinion.justificativa || null,
    pendencies: Array.isArray(opinion.pendencias)
      ? Array.from(new Set(opinion.pendencias.filter(Boolean)))
      : [],
    candidates: (opinion.candidatos ?? [])
      .map((candidate, index) => ({ candidate, index }))
      .sort(
        (a, b) =>
          (LEVEL_ORDER[a.candidate.nivel ?? ''] ?? 3) -
            (LEVEL_ORDER[b.candidate.nivel ?? ''] ?? 3) || a.index - b.index,
      )
      .map(({ candidate }) => ({
        id: candidate.id,
        name: candidate.nome,
        origin: candidate.origem ?? null,
        group: candidate.grupo ?? null,
        status: candidate.situacao ?? null,
        expiresOn: candidate.vencimento ?? null,
        level: candidate.nivel ?? null,
        downloadUrl: candidate.download_url || null,
      })),
  };
};

export const toDocumentRequirement = (
  requirement: DocumentRequirementResponse,
): DocumentRequirement => ({
  id: requirement.id,
  kind: requirement.kind,
  source: requirement.source,
  status: requirement.status,
  title: requirement.title,
  description: requirement.description,
  edictExcerpt: requirement.edict_excerpt,
  lowConfidence: requirement.confidence === 'baixa',
  confirmedBy: contactName(requirement.confirmed_by),
  ignoredBy: contactName(requirement.ignored_by),
  analystNote: requirement.analyst_note || null,
  noDocumentFound: !!requirement.no_document_found,
  analystOpinion: toAnalystOpinion(requirement.analyst_opinion),
  files: requirement.files.map((file) => ({
    id: file.id,
    name: file.filename,
    origin: file.origin,
    expiresOn: file.expires_on,
    createdBy: contactName(file.created_by),
    createdAt: file.created_at,
    downloadUrl: file.download_url,
    sourceDocumentId: file.source_document_id ?? null,
  })),
});

// Arquivo da exigência que veio deste candidato (sugestão do Analista ou documento importado).
export const attachedFileFor = (requirement: DocumentRequirement, candidateId: string) =>
  requirement.files.find((file) => file.sourceDocumentId === candidateId);

// Arquivos que não aparecem na tabela de candidatos: uploads, declarações e importados fora da lista.
export const filesOutsideCandidates = (requirement: DocumentRequirement) => {
  const candidateIds = new Set((requirement.analystOpinion?.candidates ?? []).map((c) => c.id));
  return requirement.files.filter(
    (file) => !file.sourceDocumentId || !candidateIds.has(file.sourceDocumentId),
  );
};

export const toProgress = (
  progress: DocumentRequirementsProgressResponse,
): DocumentRequirementsProgress => ({
  total: progress.total,
  confirmed: progress.confirmed,
  inReview: progress.in_review,
  pending: progress.pending,
  ignored: progress.ignored,
  percentage: progress.percentage,
});

export const toDocumentManagerDocument = (
  document: DocumentManagerDocumentResponse,
): DocumentManagerDocument => ({
  id: document.id,
  name: document.name,
  group: document.group || 'Outros documentos',
  expiresOn: document.expires_on,
  expired: document.expired,
});
